import { Injectable, signal } from '@angular/core';
import { Subject, timer } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { AuthorService } from './author.service';
import { CallRefinementPollingService } from './call-refinement-polling.service';

@Injectable({ providedIn: 'root' })
export class ScoringRefinementService {
  private editSaved$ = new Subject<string>();
  private cancel$ = new Subject<void>();
  /** callUuid whose refinement is debouncing or whose trigger request is in
   * flight — i.e. not yet handed to CallRefinementPollingService. Lets the
   * UI treat that window as "refinement in progress" too. Only one can be
   * pending at a time, since switchMap drops the previous one. */
  private readonly pendingCallUuid = signal<string | null>(null);

  constructor(
    private authorService: AuthorService,
    private callRefinementPollingService: CallRefinementPollingService
  ) {
    this.editSaved$
      .pipe(
        switchMap((callUuid) =>
          timer(3000).pipe(map(() => callUuid), takeUntil(this.cancel$))
        )
      )
      .subscribe((callUuid) => {
        this.authorService.requestCallRefinement(callUuid, '').subscribe({
          next: () => {
            // Start polling before clearing pending so there's no gap where neither reports in progress.
            this.callRefinementPollingService.start(callUuid, undefined, undefined, undefined, true);
            this.clearPending(callUuid);
          },
          error: () => this.clearPending(callUuid),
        });
      });
  }

  notifySaved(callUuid: string): void {
    this.pendingCallUuid.set(callUuid);
    this.editSaved$.next(callUuid);
  }

  cancelPending(): void {
    this.pendingCallUuid.set(null);
    this.cancel$.next();
  }

  /** Reactive read — true while a refinement for this call is scheduled but not yet polling. */
  isPending(callUuid: string): boolean {
    return !!callUuid && this.pendingCallUuid() === callUuid;
  }

  private clearPending(callUuid: string): void {
    // A newer save may already have replaced the pending callUuid — leave it alone.
    if (this.pendingCallUuid() === callUuid) this.pendingCallUuid.set(null);
  }
}
